import { ChevronRight } from 'lucide-react';
import { Sheet } from '../../components/shell/Sheet';
import { Badge, Callout, ProgressBar, cx } from '../../components/charte';
import { levelTone, themesByLevel, type RoadCodeThemeKey, type ThemeScore } from '../../lib/roadCode';
import { THEME_VISUALS } from './themeVisuals';

/** Note d'un thème sur 10, colorée selon son niveau ; tiret tant qu'il n'est pas travaillé. */
export function ScoreBadge({ theme }: { theme: ThemeScore }) {
  if (theme.score === null) return <Badge tone="mute">—/10</Badge>;
  return (
    <Badge tone={levelTone(theme.level)} size="lg" className="normal-case tracking-normal">
      <span>
        <span className="text-[13px]">{theme.score}</span>/10
      </span>
    </Badge>
  );
}

/** Pastille d'icône d'un thème. */
export function ThemeIcon({ theme, size = 'md' }: { theme: RoadCodeThemeKey; size?: 'sm' | 'md' }) {
  const { icon: Icon, tile } = THEME_VISUALS[theme];
  return (
    <span className={cx('grid shrink-0 place-items-center rounded-2xl', tile, size === 'md' ? 'h-12 w-12' : 'h-9 w-9 rounded-xl')}>
      <Icon size={size === 'md' ? 22 : 17} strokeWidth={1.9} aria-hidden />
    </span>
  );
}

/** Ligne d'un thème : icône, nom (sur deux lignes au besoin), questions à l'examen, moyenne. */
export function ThemeRow({ theme, onPick, disabled }: { theme: ThemeScore; onPick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-row border border-line bg-white p-3 text-left transition-all hover:border-primary-200 hover:shadow-navbar focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <ThemeIcon theme={theme.theme} />
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-snug text-heading">{theme.label}</span>
        <span className="mt-0.5 block text-[12.5px] text-secondary">
          {theme.examQuestions} question{theme.examQuestions > 1 ? 's' : ''} à l’examen
          {theme.score === null && ' · à découvrir'}
        </span>
        {theme.score !== null && (
          <span className="mt-1.5 block max-w-[200px]">
            <ProgressBar
              value={theme.score * 10}
              tone={theme.level === 'mastered' ? 'success' : 'primary'}
              label={`Moyenne ${theme.score} sur 10, sur vos ${theme.answers} dernières réponses`}
            />
          </span>
        )}
      </span>
      <ScoreBadge theme={theme} />
      <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden />
    </button>
  );
}

/**
 * Choix du thème d'une série thématique : thèmes rangés par niveau (à travailler d'abord), chacun
 * avec sa moyenne sur ses dernières réponses. Un thème sans question est grisé.
 */
export function ThemeSheet({
  themes,
  onPick,
  onClose,
}: {
  themes: ThemeScore[];
  onPick: (theme: RoadCodeThemeKey) => void;
  onClose: () => void;
}) {
  return (
    <Sheet title="Choisissez un thème" description="Jusqu’à 10 questions sur un thème officiel de l’examen." onClose={onClose}>
      <Callout className="mb-4">
        Votre moyenne par thème est calculée sur vos 20 dernières réponses à ce thème, toutes séries confondues (thématique, entraînement, examen
        blanc).
      </Callout>
      <div className="grid gap-5">
        {themesByLevel(themes).map((group) => (
          <section key={group.level} aria-label={group.label}>
            <h3 className="mb-2 text-[14px] font-semibold text-heading">{group.label}</h3>
            <ul className="grid gap-2">
              {group.themes.map((theme) => (
                <li key={theme.theme}>
                  <ThemeRow theme={theme} disabled={theme.questions === 0} onPick={() => onPick(theme.theme)} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Sheet>
  );
}
